import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ChevronRight, ChevronLeft, Check, User } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_ai-persona-connect/artifacts/d3pkba7d_copy_8D43DC47-40F7-432E-B350-F206250169DA.png';

const INTEREST_OPTIONS = [
  { id: 'anime', label: 'Anime', emoji: '\u2728' },
  { id: 'fantasy', label: 'Fantasy', emoji: '\u2694\ufe0f' },
  { id: 'romance', label: 'Romance', emoji: '\u2764\ufe0f' },
  { id: 'horror', label: 'Horror', emoji: '\ud83d\udc80' },
  { id: 'comedy', label: 'Comedy', emoji: '\ud83d\ude02' },
  { id: 'sci-fi', label: 'Sci-Fi', emoji: '\ud83d\ude80' },
  { id: 'adventure', label: 'Adventure', emoji: '\ud83c\udfd4\ufe0f' },
  { id: 'mystery', label: 'Mystery', emoji: '\ud83d\udd0d' },
  { id: 'slice-of-life', label: 'Slice of Life', emoji: '\u2615' },
  { id: 'action', label: 'Action', emoji: '\ud83d\udca5' },
  { id: 'drama', label: 'Drama', emoji: '\ud83c\udfad' },
  { id: 'rpg', label: 'RPG', emoji: '\ud83c\udfae' },
];

const VIBE_OPTIONS = [
  { id: 'cute', label: 'Cute & Sweet', desc: 'Wholesome & heartwarming' },
  { id: 'dark', label: 'Dark & Intense', desc: 'Gritty & dramatic' },
  { id: 'spicy', label: 'Spicy', desc: 'Flirty & bold' },
  { id: 'chill', label: 'Chill & Cozy', desc: 'Relaxed & comfy' },
  { id: 'chaotic', label: 'Chaotic & Wild', desc: 'Unpredictable fun' },
  { id: 'romantic', label: 'Romantic', desc: 'Love & passion' },
  { id: 'funny', label: 'Funny & Goofy', desc: 'Laughs guaranteed' },
  { id: 'mysterious', label: 'Mysterious', desc: 'Dark secrets' },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [step, setStep] = useState(0);
  const [interests, setInterests] = useState([]);
  const [vibes, setVibes] = useState([]);
  const [displayName, setDisplayName] = useState(user?.name || '');
  const [saving, setSaving] = useState(false);

  const toggleInterest = (id) => {
    setInterests(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : prev.length < 5 ? [...prev, id] : prev
    );
  };

  const toggleVibe = (id) => {
    setVibes(prev =>
      prev.includes(id) ? prev.filter(v => v !== id) : prev.length < 3 ? [...prev, id] : prev
    );
  };

  const completeOnboarding = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/user/onboarding`, {
        interests,
        vibes,
        display_name: displayName.trim() || user?.name,
      }, { withCredentials: true });
      await checkAuth();
      // Drop into recommended character chat for instant activation
      if (data?.recommended_character_id) {
        navigate(`/chat/${data.recommended_character_id}`, { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    } catch (err) {
      console.error('Onboarding error:', err);
      navigate('/', { replace: true });
    } finally {
      setSaving(false);
    }
  };

  const steps = [
    // Step 0: Welcome
    <div key="welcome" className="flex flex-col items-center justify-center min-h-[70vh] text-center px-8">
      <div className="relative mb-8">
        <img
          src={LOGO_URL}
          alt="Animus"
          className="w-36 h-36 object-contain drop-shadow-2xl"
          style={{ filter: 'drop-shadow(0 0 40px rgba(225,29,72,0.2))' }}
        />
      </div>
      <h1 className="text-4xl font-bold text-white font-['Unbounded'] mb-4 tracking-tight">Animus</h1>
      <p className="text-[#A1A1AA] text-base leading-relaxed max-w-[280px] mb-2">
        Chat with AI characters that actually roleplay.
      </p>
      <p className="text-[#71717A] text-sm max-w-[280px]">
        Create your own. Explore others. No limits.
      </p>
      <Button
        onClick={() => setStep(1)}
        className="mt-10 bg-[#E11D48] hover:bg-[#BE123C] text-white rounded-full px-10 py-3.5 text-base font-medium shadow-lg shadow-[#E11D48]/20 transition-all hover:shadow-xl hover:shadow-[#E11D48]/30 hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98]"
        data-testid="onboarding-start-btn"
      >
        Get Started <ChevronRight className="w-5 h-5 ml-1" />
      </Button>
    </div>,

    // Step 1: Pick interests
    <div key="interests" className="px-5 pt-8 page-enter">
      <p className="text-[10px] text-[#E11D48] font-bold uppercase tracking-[0.2em] mb-2">Step 1 of 3</p>
      <h2 className="text-2xl font-bold text-white font-['Unbounded'] mb-1 leading-tight">What are you into?</h2>
      <p className="text-sm text-[#71717A] mb-6">Pick up to 5 interests</p>
      <div className="grid grid-cols-3 gap-2.5">
        {INTEREST_OPTIONS.map(opt => {
          const selected = interests.includes(opt.id);
          return (
            <button
              key={opt.id}
              onClick={() => toggleInterest(opt.id)}
              className={`p-3.5 rounded-2xl border text-center transition-all duration-200 active:scale-95 ${
                selected
                  ? 'border-[#E11D48]/40 bg-[#E11D48]/10 shadow-inner'
                  : 'border-white/[0.06] bg-[#111318] hover:bg-white/[0.04] hover:border-white/10'
              }`}
              data-testid={`interest-${opt.id}`}
            >
              <span className="text-lg block mb-1">{opt.emoji}</span>
              <span className={`text-xs font-medium ${selected ? 'text-white' : 'text-[#A1A1AA]'}`}>{opt.label}</span>
              {selected && (
                <div className="mt-1.5 mx-auto w-5 h-5 rounded-full bg-[#E11D48] flex items-center justify-center">
                  <Check className="w-3 h-3 text-white" />
                </div>
              )}
            </button>
          );
        })}
      </div>
      <div className="flex gap-3 mt-8">
        <Button variant="ghost" onClick={() => setStep(0)} className="flex-1 text-[#71717A] hover:text-white rounded-full h-12">
          <ChevronLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <Button
          onClick={() => setStep(2)}
          disabled={interests.length === 0}
          className="flex-1 bg-[#E11D48] hover:bg-[#BE123C] text-white rounded-full h-12 disabled:opacity-30 shadow-lg shadow-[#E11D48]/20 transition-all"
          data-testid="interests-next-btn"
        >
          Next <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>,

    // Step 2: Pick vibes
    <div key="vibes" className="px-5 pt-8 page-enter">
      <p className="text-[10px] text-[#E11D48] font-bold uppercase tracking-[0.2em] mb-2">Step 2 of 3</p>
      <h2 className="text-2xl font-bold text-white font-['Unbounded'] mb-1 leading-tight">What's your vibe?</h2>
      <p className="text-sm text-[#71717A] mb-6">Pick up to 3</p>
      <div className="grid grid-cols-2 gap-2.5">
        {VIBE_OPTIONS.map(opt => {
          const selected = vibes.includes(opt.id);
          return (
            <button
              key={opt.id}
              onClick={() => toggleVibe(opt.id)}
              className={`p-4 rounded-2xl border text-left transition-all duration-200 active:scale-95 ${
                selected
                  ? 'border-[#E11D48]/40 bg-[#E11D48]/10'
                  : 'border-white/[0.06] bg-[#111318] hover:bg-white/[0.04] hover:border-white/10'
              }`}
              data-testid={`vibe-${opt.id}`}
            >
              <span className={`text-sm font-semibold block ${selected ? 'text-white' : 'text-[#D4D4D8]'}`}>{opt.label}</span>
              <span className="text-[11px] text-[#71717A] block mt-0.5">{opt.desc}</span>
              {selected && (
                <div className="mt-2 w-5 h-5 rounded-full bg-[#E11D48] flex items-center justify-center">
                  <Check className="w-3 h-3 text-white" />
                </div>
              )}
            </button>
          );
        })}
      </div>
      <div className="flex gap-3 mt-8">
        <Button variant="ghost" onClick={() => setStep(1)} className="flex-1 text-[#71717A] hover:text-white rounded-full h-12">
          <ChevronLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <Button
          onClick={() => setStep(3)}
          disabled={vibes.length === 0}
          className="flex-1 bg-[#E11D48] hover:bg-[#BE123C] text-white rounded-full h-12 disabled:opacity-30 shadow-lg shadow-[#E11D48]/20 transition-all"
          data-testid="vibes-next-btn"
        >
          Next <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>,

    // Step 3: Display name
    <div key="name" className="px-5 pt-8 page-enter">
      <p className="text-[10px] text-[#E11D48] font-bold uppercase tracking-[0.2em] mb-2">Step 3 of 3</p>
      <h2 className="text-2xl font-bold text-white font-['Unbounded'] mb-1 leading-tight">What should characters call you?</h2>
      <p className="text-sm text-[#71717A] mb-8">This is used in roleplay conversations</p>
      <div className="flex items-center gap-4 mb-8">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#E11D48]/20 to-[#F59E0B]/20 border border-white/10 flex items-center justify-center flex-shrink-0">
          <User className="w-7 h-7 text-[#A1A1AA]" />
        </div>
        <Input
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="Your name"
          maxLength={30}
          autoFocus
          className="bg-[#111318] border-white/10 text-white placeholder:text-[#555] rounded-2xl text-lg h-14 focus:border-[#E11D48]/50"
          data-testid="onboarding-name-input"
        />
      </div>
      <div className="flex gap-3">
        <Button variant="ghost" onClick={() => setStep(2)} className="flex-1 text-[#71717A] hover:text-white rounded-full h-12">
          <ChevronLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <Button
          onClick={completeOnboarding}
          disabled={saving}
          className="flex-1 bg-gradient-to-r from-[#E11D48] to-[#9333EA] text-white rounded-full h-12 font-medium shadow-lg shadow-[#E11D48]/20 transition-all hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98]"
          data-testid="onboarding-finish-btn"
        >
          {saving ? 'Setting up...' : "Let's Go!"}
        </Button>
      </div>
    </div>,
  ];

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      {/* Progress bar */}
      {step > 0 && (
        <div className="px-6 pt-6">
          <div className="h-1 bg-white/[0.04] rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#E11D48] to-[#9333EA] rounded-full transition-all duration-500 ease-out"
              style={{ width: `${(step / 3) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Skip button */}
      {step > 0 && (
        <div className="flex justify-end px-6 pt-3">
          <button
            onClick={() => { completeOnboarding(); }}
            className="text-xs text-[#555] hover:text-[#A1A1AA] transition-colors py-1 px-2"
            data-testid="skip-onboarding-btn"
          >
            Skip for now
          </button>
        </div>
      )}

      {steps[step]}
    </div>
  );
}
